/**
 * Full-screen "service disabled" page. Rendered by <ServiceGate> when
 * `service.enabled === false`.
 *
 * Contents (top to bottom):
 *
 *   - Large maintenance glyph
 *   - notice.title (operator-controlled)
 *   - notice.message (operator-controlled, supports plain text + line breaks)
 *   - "了解更多" link → notice.learnMoreUrl when set
 *   - Captures escape hatch: a small link reminding users they can still
 *     download historical screenshots / recordings (the GET /api/files/*
 *     paths are whitelisted by the kill-switch middleware)
 *   - "重新检查" retry button → force-refetches /api/config
 *   - Tiny footer with helper version + boot timestamp so support can
 *     triage which build the user is on
 */
import { useState } from 'react';
import { AlertTriangle, RefreshCw, FolderOpen, ExternalLink } from 'lucide-react';
import clsx from 'clsx';
import { useRuntimeConfig } from '@/lib/runtimeConfig';

export function MaintenanceScreen(): JSX.Element {
  const notice = useRuntimeConfig((s) => s.config.service.notice);
  const version = useRuntimeConfig((s) => s.config.version);
  const loadedAt = useRuntimeConfig((s) => s.config.loadedAt);
  const source = useRuntimeConfig((s) => s.source);
  const fetchOnce = useRuntimeConfig((s) => s.fetchOnce);
  const lastError = useRuntimeConfig((s) => s.lastError);
  const [refreshing, setRefreshing] = useState(false);

  const onRetry = async () => {
    setRefreshing(true);
    try {
      await fetchOnce();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex h-full w-full items-center justify-center bg-bg p-6 text-ink">
      <div className="w-full max-w-xl rounded-xl border border-line bg-bg-card p-8 shadow-card">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-amber-100 p-3 dark:bg-amber-500/10">
            <AlertTriangle className="h-7 w-7 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold text-ink">
              {notice.title || '服务公告'}
            </h1>
            {notice.message ? (
              <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-ink-muted">
                {notice.message}
              </p>
            ) : (
              <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                服务暂时不可用。请稍后重试，或联系管理员了解恢复时间。
              </p>
            )}

            {notice.learnMoreUrl ? (
              <a
                href={notice.learnMoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-sm text-accent hover:text-accent/80"
              >
                了解更多
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : null}
          </div>
        </div>

        <div className="mt-6 rounded-lg border border-line bg-bg-soft p-4 text-xs text-ink-muted">
          <div className="flex items-start gap-2">
            <FolderOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-dim" />
            <p>
              历史截图 / 录屏 / 文件抓取仍可下载（位于本地 devices
              目录），不受本次停服影响。
            </p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onRetry}
            disabled={refreshing}
            className={clsx(
              'inline-flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-sm font-medium text-white shadow-card transition',
              'hover:bg-accent/90 disabled:cursor-wait disabled:opacity-60',
            )}
          >
            <RefreshCw className={clsx('h-3.5 w-3.5', refreshing && 'animate-spin')} />
            {refreshing ? '检查中…' : '重新检查'}
          </button>
          <div className="text-right text-[11px] leading-tight text-ink-dim">
            <div>服务版本 v{version}</div>
            <div>
              {source === 'server'
                ? `已联通服务 · ${loadedAt ? new Date(loadedAt).toLocaleTimeString() : ''}`
                : source === 'cache'
                  ? '使用本地缓存（无法联通服务）'
                  : '使用默认值'}
            </div>
            {lastError ? (
              <div className="mt-0.5 text-amber-700 dark:text-amber-400">
                最近错误（{lastError.kind}）：{lastError.message}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
